<template>
  <div class="list-tab has-radius-top-left">
    <div class="column">
      <div class="columns">
        <div class="column is-3 tabs">
          <div class="tabs-veritcal">
            <div
              v-for="(tab, index) in reqTabs"
              :key="index"
              class="tab-item"
              :class="{ active: activeTab === index }"
              @click="activeTab = index"
            >
              <div class="tab-icon is-pulled-left is-flex is-align-items-center is-justify-content-center">
                <i :class="tab.icon"></i>
              </div>
              <h3 class="has-text-black-bis is-size-3 has-text-weight-bold line-height">
                {{ tab.title }}
              </h3>
              <p class="is-size-5 has-text-black-bis">{{ tab.subTitle }}</p>
            </div>
          </div>
        </div>

        <!-- Right Side: Tab Content -->
        <div class="column">
          <template v-if="reqTabs.length">
            <div class="is-flex is-align-items-center is-justify-content-space-between has-background-grey-lighter">
              <h3 class="has-text-black-bis has-text-weight-bold is-size-3">
                {{ reqTabs[activeTab].title }}
              </h3>
              <div class="is-flex is-align-items-center">
                <div class="field has-addons mb-0 mr-3">
                   <i
                    class="icon-activityhistory pointer has-text-black-bis"
                    @click="isActivityActive = true"
                  ></i>
                  <div class="is-divider-vertical"></div>
                  <i
                    class="icon-addnew pointer has-text-black-bis"
                    v-permission="{ permissionModule: 'app&projects', type: 'lists' }"
                    @click="triggerAdd"
                  ></i>
                 
                  <i
                    v-if="['Material Categories', 'Tags'].includes(reqTabs[activeTab].title)"
                    class="icon-importexcel pointer has-text-black-bis"
                    v-permission="{ permissionModule: 'app&projects', type: 'lists' }"
                    @click="triggerImport"
                  ></i>
                  <div class="is-divider-vertical"></div>
                  <div class="control">
                    <search-bar
                      :placeholder="'Search'"
                      :shouldEmit="true"
                      :searchValue="searchText"
                      @search="onSearch"/>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="tab-content has-background-grey-lighter p-3 "
              v-if="reqTabs[activeTab].title === 'Work Steps'"
            >
              <WorkSteps ref="workStepsRef" :searchText="searchText" />
            </div>
            <div
              class="tab-content has-background-grey-lighter p-3"
              v-if="reqTabs[activeTab].title === 'Material Categories'"
            >
              <MaterialCategories ref="categoriesRef" :searchText="searchText" />
            </div>
            <div
              class="tab-content has-background-grey-lighter p-3"
              v-if="reqTabs[activeTab].title === 'Tags'"
            >
              <Tags ref="tagsRef" :projId="projId" :searchText="searchText" />
            </div>
            <div
              class="tab-content has-background-grey-lighter p-3"
              v-if="reqTabs[activeTab].title === 'Measure Units'"
            >
              <MeasureUnits ref="measureUnitsRef" :searchText="searchText" />
            </div>
            <div
              class="tab-content has-background-grey-lighter p-3"
              v-if="reqTabs[activeTab].title === 'QA/QC Issue Types'"
            >
              <QaQcIssueTypes ref="qaQcRef" :searchText="searchText" />
            </div>
          </template>
        </div>
         <activity-slider
          :isActive="isActivityActive"
          :id="companyId"
          module="Company"
          :company="companyId"
          @update:isActive="isActivityActive = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, toRefs, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { filter } from 'lodash';
import WorkSteps from '@/components/list-tabs/WorkSteps.vue';
import MaterialCategories from '@/components/list-tabs/MaterialCategories.vue';
import Tags from '@/components/list-tabs/Tags.vue';
import MeasureUnits from '@/components/list-tabs/MeasureUnits.vue';
import QaQcIssueTypes from '@/components/list-tabs/QaQcIssueTypes.vue';
import SearchBar from '@/components/SearchBar.vue';
import ActivitySlider from '@/components/ActivitySlider.vue';

export default {
  name: 'Lists',

  components: {
    WorkSteps,
    MaterialCategories,
    Tags,
    MeasureUnits,
    QaQcIssueTypes,
    SearchBar,
    ActivitySlider,
  },

  props: {
    projId: {
      type: String,
      default: null,
    },
    tabsToShow: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const store = useStore();
    const route = useRoute();
    const data = reactive({
      workStepsRef: null,
      categoriesRef: null,
      tagsRef: null,
      measureUnitsRef: null,
      qaQcRef: null,
      searchText: '',
      isActivityActive: false,
      activeTab: 0,
    });

    const listTabs = [
      {
        title: 'Work Steps',
        subTitle: 'Define and edit Work Steps',
        icon: 'icon-attachednotesOL',
      },
      {
        title: 'Material Categories',
        subTitle: 'Define and edit Material Categories',
        icon: 'icon-attachedmaterialsOL',
      },
      {
        title: 'Tags',
        subTitle: 'Define and edit Company Tags',
        icon: 'icon-attachedtagsOL',
      },
      {
        title: 'Measure Units',
        subTitle: 'Define and edit Measure Units',
        icon: 'icon-attachedtrackingnumbersOL',
      },
      {
        title: 'QA/QC Issue Types',
        subTitle: 'Define and edit QA/QC Issue Types',
        icon: 'icon-qcinspect',
      },
    ];

    const reqTabs = computed(() =>
      props.tabsToShow.length
        ? filter(listTabs, (t) => props.tabsToShow.includes(t.title))
        : listTabs
    );
    const companyId = computed(() => store.state.userData?.company || '');

    const activeRef = computed(() => {
      const title = reqTabs.value[data.activeTab]?.title;
      if (title === 'Work Steps') return data.workStepsRef;
      if (title === 'Material Categories') return data.categoriesRef;
      if (title === 'Tags') return data.tagsRef;
      if (title === 'Measure Units') return data.measureUnitsRef;
      if (title === 'QA/QC Issue Types') return data.qaQcRef;
      return null;
    });

    const triggerAdd = () => {
      activeRef.value?.openModal({});
    };

    const triggerImport = () => {
      activeRef.value?.importExcel?.();
    };
   
    const onSearch = (value) => {
      data.searchText = value;
      activeRef.value?.refreshTable?.();
    };

    onMounted(async () => {
      await store.getters.userPromise;
      if (route.query.currentTab) {
        const queryIndex = parseInt(route.query.currentTab, 10);
        if (queryIndex >= 0 && queryIndex < reqTabs.value.length) {
          data.activeTab = queryIndex;
        }
      } else {
        data.activeTab = 0;
      }
    });

    return {
      ...toRefs(data),
      reqTabs,
      onSearch,
      triggerAdd,
      triggerImport,
      companyId,
    };
  },
};
</script>